import { memo, useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'core/store/hooks';
import { SelectItem, SelectList } from 'app/components/SelectList';
import { IVariable, IVariableDAO, VariableType } from 'types/domain/variable';
import { update } from 'core/actions/admin/data';
import { EntityTypes } from 'constants/entities';
import { IActionBasic } from 'types/domain/action';
import InputField from 'app/components/InputField';
import { selectOfflineId } from 'core/selectors/admin/offlineId';
import { createRequest, getRequest } from 'core/services/admin';
import { selectVariable } from 'core/selectors/admin/variable';
import { selectCourseVariables } from 'core/selectors/admin/courseVariables';
import { ICourseVariableDAO } from 'types/domain/course';
import { HandThumbUpIcon } from '@heroicons/react/24/outline';
import { selectModuleVariable } from 'core/selectors/product/module';
import { VariableTypeIcon } from 'app/pages/Create/Courses/Course/Constants/variable';

export const ActionVariableSelector = memo(({ action, onChange }: { action: IActionBasic; onChange: (val) => {} }) => {
  const dispatch = useAppDispatch();
  const offlineId = useAppSelector(selectOfflineId);
  const moduleVariable: IVariableDAO = useAppSelector(selectModuleVariable);

  useEffect(() => {
    dispatch(getRequest({ type: EntityTypes.VARIABLE }));
  }, [dispatch]);
  const courseId = useAppSelector(state => state.state[EntityTypes.COURSE]);
  const variables: ICourseVariableDAO[] = useAppSelector(selectCourseVariables);
  const updateVariable = useCallback(
    val =>
      dispatch(
        update({
          type: EntityTypes.VARIABLE,
          parentModuleId: action.moduleId,
          parentEntityType: EntityTypes.MODULE,
          entity: { id: action.variable.id, ...val },
        }),
      ),
    [action, dispatch],
  );
  const onChangeVariable = variableId => onChange({ variableId, value: null, variableOptionId: null });

  const newVariable = () => {
    dispatch(
      createRequest({
        type: EntityTypes.VARIABLE,
        parentEntityAttribute: 'variableId',
        parentEntityType: EntityTypes.ACTION,
        parentEntityId: action.id,
        entity: { id: offlineId, offlineId, courseId },
      }),
    );
  };
  const items = useMemo(() => {
    return [
      ...variables
        .filter(variable => !variable.variable.moduleId)
        .map(courseVariable => {
          return {
            name: courseVariable.variable.name,
            value: courseVariable.variable.id,
            component: (
              <>
                <VariableIcon type={courseVariable.variable.type} className="inline-flex mr-2 w-5 h-5" />{' '}
                <span>{courseVariable.variable.text?.shortText}</span>
              </>
            ),
          } as SelectItem;
        }),
      {
        name: 'No record',
        component: (
          <>
            <VariableIcon type={moduleVariable.type} className="inline-flex mr-2 w-5 h-5" />{' '}
            <span>{moduleVariable?.text?.shortText}</span>
          </>
        ),
        value: null,
      },
    ];
  }, [moduleVariable?.text?.shortText, moduleVariable?.type, variables]);

  if (!action) return null;

  return (
    <>
      <div className="flex  h-full justify-center items-center w-full">
        {items.length ? (
          <SelectList
            label="Select record type"
            className="h-full text-md w-full"
            items={items}
            value={action?.variableId}
            //search={true}
            listClassName="w-full"
            labelClassName="h-full w-full items-center flex whitespace-nowrap  truncate"
            onChange={onChangeVariable}
          />
        ) : null}
      </div>
    </>
  );
});

export function VariableValue({
  value,
  variableOptionId,
  variable,
  onChange,
  className,
}: {
  value: number;
  variableOptionId: number;
  variable: IVariable;
  onChange: (val) => void;
  className?: string;
}) {
  const variableDAO = useAppSelector(state => selectVariable(state, variable?.id));

  const element = useMemo(() => {
    if (!variable || !variableDAO) return null;

    switch (variable.type) {
      case VariableType.BOOLEAN:
        return (
          <div onClick={e => onChange({ value: value === 1 ? 0 : 1 })} className="w-5 h-5 ">
            {value === 1 ? <HandThumbUpIcon /> : null}
          </div>
        );
      case VariableType.SCORE:
        return (
          <InputField
            className="w-20 mx-2 rounded "
            inputClassName=" w-full"
            type={'number'}
            onChange={value => onChange({ value: parseFloat(value) })}
            value={value || ''}
            name={'value'}
            placeholder={'0'}
          />
        );
      case VariableType.OPTIONS:
        if (variableDAO.options)
          return (
            <SelectList
              items={variableDAO.options
                .map(opt => {
                  return { name: opt.name, value: opt.id };
                })
                .concat({ name: 'No Option', value: null })}
              className="w-full relative"
              listClassName="w-full"
              labelClassName="whitespace-nowrap block truncate"
              label={'Select option'}
              value={variableOptionId}
              onChange={variableOptionId => onChange({ variableOptionId })}
            />
          );
    }
  }, [onChange, value, variable, variableDAO, variableOptionId]);

  return element;
}

export function VariableIcon({ type, className }) {
  return VariableTypeIcon(type, className);
}
